/* eslint-disable @typescript-eslint/no-explicit-any */
import { ViewportScroller } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup
} from '@angular/forms';
import {
  ApiService,
  FiltersType,
  ICampaignFilterCriteria,
  IChannel,
  IChannels,
  IChannelsMappedAndUnmappedCount,
  IFilterCriteria,
  IFlattenedBrand,
  ISegments,
  ITactics,
  IUpdateCampaignWithFilterCriteria,
  IVCampaign
} from '@libs/apis';
import {
  IPTableColumnInfo,
  ITitle,
  TitleTypes,
  ViewType
} from '@libs/common-components';
import { get, uniq } from 'lodash-es';
import { ConfirmationService, LazyLoadEvent } from 'primeng/api';
import { TaxonomyHelper } from '../helper/taxonomy-helper';
import { TableMetadataService } from '../services/table-metadata.service';
import { TaxonomyReviewPublishComponent } from './taxonomy-review-publish/taxonomy-review-publish.component';
import { Status } from './taxonomy-review-publish/taxonomy-review-publish.constants';
import { TaxonomyUiFactory } from './taxonomy-ui-factory';
import {
  CampaignDimension,
  TaxonomyUiConstants
} from './taxonomy-ui.constants';

@Component({
  selector: 'lib-taxonomy-taxonomy-ui',
  templateUrl: './taxonomy-ui.component.html',
  styleUrls: ['./taxonomy-ui.component.scss'],
  animations: TaxonomyUiConstants.updateTacticOverlayTriggerAnimations
})
export class TaxonomyUiComponent implements OnInit {
  @ViewChild('reviewNPublish')
  taxonomyReviewNPublish?: TaxonomyReviewPublishComponent;

  public readonly columns = TaxonomyUiConstants.dataSourceChannelTableColumns;
  public readonly headerConfig = TaxonomyUiConstants.headerConfig;
  public readonly filterCriteria = TaxonomyUiConstants.filterCriteria;
  public readonly dataSourcesTitleConfigs =
    TaxonomyUiConstants.dataSourcesTitleConfigs;

  public campaignColumns = [
    TaxonomyUiConstants.checkboxTableColumn,
    ...TaxonomyUiConstants.defaultCampaignTableColumns
  ];

  private clientId = 0;
  private brandId = 0;
  private userDetails: IFlattenedBrand | null = null;
  private reportingDimensionConfigs: IPTableColumnInfo[] = [];

  public campaignsCountByChannel: IChannelsMappedAndUnmappedCount[] = [];
  public searchValue = '';
  public campaigns: IVCampaign[] = [];
  public loader: Record<string, boolean> = {};
  public campaignTypeOptions: Record<string, string | boolean>[] = [];
  public selectedDataSource = '';
  public selectedCampaignType = '';
  public campaignTableTitleConfigs: ITitle[] = [];
  public filterCriteriaForm: UntypedFormGroup = new UntypedFormGroup({});
  public newTacticForm: UntypedFormGroup = new UntypedFormGroup({});
  public newChannelForm: UntypedFormGroup = new UntypedFormGroup({});
  public displayCampaignCountSearchBox: Record<string, boolean> = {};
  public displayCampaignSearchBox: Record<string, boolean> = {};
  public displayUpdateTacticDialog = false;
  public displayNewTacticDialog = false;
  public displayNewChannelDialog = false;
  public tactics: ITactics[] = [];
  public selectedCampaignsCount = 0;
  public selectedTactics: string[] = [];
  public selectedSegments: string[] = [];
  public selectedChannels: string[] = [];
  public selectedTacticForUpdate: string | undefined = '';
  public selectedSegmentForUpdate: string | undefined = '';
  public selectedChannelForUpdate: string | undefined = '';
  public isNewTacticExisting = false;
  public isNewChannelExisting = false;
  public selectedCampaigns: IVCampaign[] = [];
  public loaderTitle = '';
  public loaderMessage = '';
  public loaderUiView: ViewType = ViewType.empty;
  public totalCampaigns = 0;
  public campaignsInView: IVCampaign[] = [];
  public currentPageOfCampaigns = 0;
  public selectAll = false;
  private removeTacticAction = false;
  private underProcessingCampaignCount = 0;
  private multipleTacticCampaignsCount = 0;
  public campaignFactory = TaxonomyUiConstants.campaignFactory;
  public searchTactics: string[] = [];
  public searchChannels: string[] = [];
  public filterList: Record<string, string[]> = {};
  public showAdvanceFilterDialog = false;
  public advanceFiltersForm: UntypedFormGroup = new UntypedFormGroup({});
  public isAdvanceFilterApplied = false;
  public isAdvanceFilterSaved = false;
  public segments: ISegments[] = [];
  public isTaxonomyReviewPublishCard = false;
  public confirmDialogReviewButtonLabel = '';
  public clientTimeZone = 'US/Eastern';

  public actionButtonConfig = [
    TaxonomyUiConstants.reviewButtonConfig,
    TaxonomyUiConstants.publishButtonConfig
  ];

  public channels: IChannels[] = [];
  public reviewNPublishCount: Record<string, number> = {};
  public selectedCampaignByTempRecordId: Record<string, IVCampaign> = {};

  constructor(
    private readonly apiService: ApiService,
    private readonly fb: UntypedFormBuilder,
    private readonly viewportScroller: ViewportScroller,
    private readonly confirmationService: ConfirmationService,
    private readonly tableMetadataService: TableMetadataService
  ) {}

  ngOnInit() {
    this.getUserDetails();
    this.getCampaignsCount();
    this.initializeForms();
    this.getCampaignsCountByStatus();
  }

  private getUserDetails(): void {
    this.userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    this.clientId = this.userDetails?.clientId || 0;
    this.brandId = this.userDetails?.brandId || 0;
    this.clientTimeZone = this.userDetails?.geography || this.clientTimeZone;
  }

  public getCampaignsCount(): void {
    this.loader['datasources'] = true;
    this.loaderUiView = ViewType.loader;
    this.loaderTitle = `We're on it!`;
    this.loaderMessage = 'Retrieving campaign mapping information';
    this.apiService.taxonomyApisOperations
      .getCampaignCountWithFilter(this.clientId, this.brandId, {
        clientId: this.clientId,
        brandId: this.brandId,
        includeInProgress: false
      })
      .then((campaignsCount) => {
        this.campaignsCountByChannel = campaignsCount;
        this.loaderMessage = 'Retrieved campaign mapping information';
        setTimeout(() => {
          this.handleCampaignCountLoaderClose();
        }, TaxonomyUiConstants.waitTimeInMilliseconds);
      })
      .catch((error) => {
        console.error('Error: ', error.message);
        this.loaderTitle = 'Error!';
        this.loaderMessage = 'Failed to retrieve campaign mapping information';
        this.loaderUiView = ViewType.error;
      });
  }

  private initializeForms(): void {
    const taxonomyUIFactory = new TaxonomyUiFactory(this.fb);
    this.filterCriteriaForm = taxonomyUIFactory.getFilterCriteriaForm();
    this.newTacticForm = taxonomyUIFactory.getNewTacticForm();
    this.advanceFiltersForm = taxonomyUIFactory.getAdvanceFilterForm();
    this.newTacticForm.valueChanges.subscribe((data) =>
      this.isTacticExisting(data)
    );
    this.newChannelForm = taxonomyUIFactory.getNewChannelcForm();
    this.newChannelForm.valueChanges.subscribe((data) =>
      this.isChannelExisting(data)
    );
  }

  public isRowSelectable(event: any): boolean {
    return event.data.isSelectable;
  }

  public showCampaigns(rowData: any, campaignType: string): void {
    if (
      !rowData[campaignType] ||
      this.loader[TaxonomyUiConstants.updateTactic] ||
      this.showCampaignsForSameDatasourceAndCampaignType(rowData, campaignType)
    ) {
      return;
    }

    this.selectedDataSource = rowData.compositeDataSourceName;
    this.selectedCampaignType = campaignType;

    this.campaignTableTitleConfigs = [
      {
        title: this.selectedDataSource,
        type: TitleTypes.h2,
        subTitle: null,
        styleClass: 'text-orange fw-light',
        subTitleStyleClass: 'text-dark fw-light'
      },
      {
        title: `
                <div class="mb-2">Select campaigns to map to channel, segment and tactic.</div>
                <div class="fs-14">Please note: Campaigns with only segment dimension left unassigned are visible under Mapped campaigns with value 'Others' after the changes are approved and published. You can search for specific campaigns at the top of every column.</div>
              `,
        type: TitleTypes.info,
        subTitle: null,
        styleClass: 'text-gray fw-normal'
      }
    ];

    this.totalCampaigns =
      campaignType === TaxonomyUiConstants.mappedCount
        ? rowData.mappedCount
        : rowData.unmappedCount;

    this.campaignTypeOptions = [
      {
        label: `Mapped Campaigns (${rowData.mappedCount})`,
        value: TaxonomyUiConstants.mappedCount,
        disable: !rowData.mappedCount
      },
      {
        label: `Unmapped Campaigns (${rowData.unmappedCount})`,
        value: TaxonomyUiConstants.unmappedCount,
        disable: !rowData.unmappedCount
      }
    ];

    this.abortTacticUpdate();
    this.resetFilterCriteriaForm();
    this.showCampaignsTable();
    this.getCampaignsAndTactics();
    this.abortTacticUpdate();
    this.tableMetadataService
      .getCompositeDSReportingDimensionConfigs(this.selectedDataSource)
      .then((columns) => (this.reportingDimensionConfigs = columns));
    this.setOrResetAdvanceFilters();
  }

  public getCampaignsAndTactics(): void {
    this.campaigns = [];
    this.loader['campaigns-tactic'] = true;
    this.loaderTitle = `We're on it!`;
    this.loaderUiView = ViewType.loader;
    this.loaderMessage =
      TaxonomyUiConstants.fetchingCampaignsLoaderMessage[
        this.selectedCampaignType
      ] || '';
    const postBody = this.getFilterCampaignsBody();
    const selectedDataSource = encodeURIComponent(this.selectedDataSource);

    Promise.all([
      this.apiService.taxonomyApisOperations.getCampaignsByDatasourceNChannel(
        this.clientId,
        this.brandId,
        postBody
      ),
      this.apiService.taxonomyApisOperations.getTacticsByDatasource(
        this.clientId,
        this.brandId,
        selectedDataSource
      ),
      this.apiService.taxonomyApisOperations.getUnderProcessingCampaignsCount(
        this.clientId,
        this.brandId,
        selectedDataSource
      ),
      this.apiService.taxonomyApisOperations.getMultipleTacticCampaignsCount(
        this.clientId,
        this.brandId,
        selectedDataSource
      ),
      this.apiService.taxonomyApisOperations.getSegments(
        this.clientId,
        this.brandId,
        selectedDataSource
      ),
      this.apiService.taxonomyApisOperations.getChannels(
        this.clientId,
        this.brandId,
        selectedDataSource
      )
    ])
      .then(
        ([
          campaigns,
          tactics,
          underProcessingCampaignCount,
          multipleTacticCampaignCount,
          segments,
          channels
        ]) => {
          campaigns.forEach((campaign) => {
            campaign.isSelectable =
              campaign.tactic !== TaxonomyUiConstants.multipleTactics;
          });
          this.campaigns = campaigns;
          this.campaignsInView = campaigns;
          this.currentPageOfCampaigns = 0;
          this.addColumns();
          this.resetSelectedMappingDropdown();
          this.tactics = tactics;
          this.tactics[this.tactics.length - 1]?.items.push({
            type: 'divider',
            disabled: true
          });
          this.tactics[this.tactics.length - 1]?.items.push({
            type: 'button',
            tactic: TaxonomyUiConstants.createNewTactic
          });
          this.segments = segments;
          this.channels = channels;
          this.channels[this.channels.length - 1]?.items.push({
            type: 'divider',
            disabled: true
          });
          this.channels[this.channels.length - 1]?.items.push({
            type: 'button',
            channel: TaxonomyUiConstants.createNewChannel
          });
          this.underProcessingCampaignCount =
            underProcessingCampaignCount.count;
          this.multipleTacticCampaignsCount = multipleTacticCampaignCount.count;
          this.setTotalCampaignsOnCampaignTypeChange();
          this.loaderTitle = TaxonomyUiConstants.retrievingCampaigns;
          this.loaderMessage =
            campaigns.length === TaxonomyUiConstants.defaultLazyLoadingSize &&
            !this.isFilterApplied()
              ? `Showing first 1,000 campaigns out of ${this.totalCampaigns.toLocaleString()}`
              : `${campaigns.length.toLocaleString()} ${this.getCampaignString(
                  campaigns.length
                )} retrieved`;
          setTimeout(() => {
            this.handleCampaignDetailsLoaderClose();
          }, TaxonomyUiConstants.waitTimeInMilliseconds);
        }
      )
      .catch((error) => {
        console.error('Error: ', error.message);
        this.loaderTitle = 'Error!';
        this.loaderMessage = TaxonomyUiConstants.failureMessage;
        this.loaderUiView = ViewType.error;
      });
  }

  private showCampaignsForSameDatasourceAndCampaignType(
    rowData: any,
    campaignType: string
  ): boolean {
    return (
      rowData.dataSource === this.selectedDataSource &&
      campaignType === this.selectedCampaignType
    );
  }

  public getCampaigns(): void {
    this.campaigns = [];
    this.loader['campaigns'] = true;
    this.loaderUiView = ViewType.loader;
    this.loaderTitle = `We're on it!`;
    this.loaderMessage =
      TaxonomyUiConstants.fetchingCampaignsLoaderMessage[
        this.selectedCampaignType
      ] || '';
    const postBody = this.getFilterCampaignsBody();
    this.campaigns = [];
    this.campaignsInView = [];
    this.apiService.taxonomyApisOperations
      .getCampaignsByDatasourceNChannel(this.clientId, this.brandId, postBody)
      .then((campaigns) => {
        campaigns.forEach((campaign) => {
          campaign.isSelectable =
            campaign.tactic !== TaxonomyUiConstants.multipleTactics;
        });
        this.campaigns = campaigns;
        if (
          this.isFilterApplied() ||
          (this.isAdvanceFiltersApplied() && this.isAdvanceFilterApplied)
        ) {
          this.totalCampaigns = campaigns.length;
        } else {
          this.setTotalCampaignsOnCampaignTypeChange();
        }
        this.campaignsInView = campaigns;
        this.currentPageOfCampaigns = 0;
        this.loaderTitle = TaxonomyUiConstants.retrievingCampaigns;
        this.loaderMessage =
          this.isFilterApplied() ||
          (this.isAdvanceFiltersApplied() && this.isAdvanceFilterApplied)
            ? `${campaigns.length.toLocaleString()} ${this.getCampaignString(
                campaigns.length
              )} retrieved`
            : `Showing first 1,000 campaigns out of ${this.totalCampaigns.toLocaleString()}`;
        this.addColumns();
        this.resetSelectedMappingDropdown();
        this.filterList = {};
        this.showAdvanceFilterDialog = false;
        this.isAdvanceFilterApplied = false;
        setTimeout(() => {
          this.handleCampaignDetailsLoaderClose();
        }, TaxonomyUiConstants.waitTimeInMilliseconds);
      })
      .catch((error) => {
        console.error('Error: ', error.message);
        this.loaderTitle = 'Error!';
        this.loaderMessage = TaxonomyUiConstants.failureMessage;
        this.loaderUiView = ViewType.error;
      });
  }

  private addColumns(): void {
    if (this.selectedCampaignType === TaxonomyUiConstants.unmappedCount) {
      this.campaignColumns = [
        TaxonomyUiConstants.checkboxTableColumn,
        ...this.reportingDimensionConfigs,
        TaxonomyUiConstants.channelTableColumn,
        TaxonomyUiConstants.segmentTableColumn,
        TaxonomyUiConstants.tacticTableColumn,
        TaxonomyUiConstants.recommendedTacticTableColumn,
        TaxonomyUiConstants.recommendedChannelTableColumn,
        TaxonomyUiConstants.recommendedSegmentTableColumn
      ];
    } else {
      this.campaignColumns = [
        TaxonomyUiConstants.checkboxTableColumn,
        ...this.reportingDimensionConfigs,
        TaxonomyUiConstants.channelTableColumn,
        TaxonomyUiConstants.segmentTableColumn,
        TaxonomyUiConstants.tacticTableColumn,
        TaxonomyUiConstants.recommendedTacticTableColumn,
        TaxonomyUiConstants.recommendedChannelTableColumn,
        TaxonomyUiConstants.recommendedSegmentTableColumn
      ];
    }
  }

  private getFilterCampaignsBody(): ICampaignFilterCriteria {
    return {
      dataSourceId: this.selectedDataSource,
      campaignType:
        TaxonomyUiConstants.campaignTypeFactory[this.selectedCampaignType] ||
        '',
      filterCriteria: this.isAdvanceFilterApplied
        ? this.prepareFilterCriteriaForAdvanceFilters()
        : this.prepareFilterCriteria(),
      offset: this.campaigns?.length || 0
    };
  }

  private prepareFilterCriteria(): IFilterCriteria[] {
    const filterCriteria: IFilterCriteria[] = [];
    const filterCriteriaFormValue = this.filterCriteriaForm.getRawValue();
    const scopes = Object.keys(filterCriteriaFormValue);
    scopes.forEach((scope) => {
      const filterValue = filterCriteriaFormValue[`${scope}FilterValue`];
      if (filterValue) {
        filterCriteria.push({
          fieldName: scope,
          value: filterValue.trim(),
          filter: filterCriteriaFormValue[scope]
        });
      }
    });
    return filterCriteria;
  }

  private prepareFilterCriteriaForAdvanceFilters(): IFilterCriteria[] {
    const filterCriteria: IFilterCriteria[] = [];
    const advanceFiltersFormValues = this.advanceFiltersForm.getRawValue();
    const dimensions = Object.keys(advanceFiltersFormValues);
    dimensions.forEach((dimension) => {
      const filters: Record<string, string | boolean>[] =
        advanceFiltersFormValues[dimension];
      filters.forEach((filter) => {
        if (
          filter[dimension] &&
          this.campaignColumns.some((col) => col.field === dimension)
        ) {
          filterCriteria.push({
            fieldName: dimension,
            value: filter[dimension] as string,
            filter: filter[`is${dimension}Negative`]
              ? FiltersType.NOT_CONTAINS
              : FiltersType.CONTAINS
          });
        }
      });
    });
    return filterCriteria;
  }

  public resetFilterCriteriaForm(): void {
    this.filterCriteriaForm.enable();
    Object.keys(this.displayCampaignSearchBox).forEach(
      (key) => (this.displayCampaignSearchBox[key] = false)
    );
    this.filterCriteriaForm = new TaxonomyUiFactory(
      this.fb
    ).getFilterCriteriaForm();
    this.filterCriteriaForm.markAsTouched();
    this.newTacticForm.reset();
    this.newTacticForm.markAsTouched();
  }

  public showCampaignsTable(): void {
    const showCampaignsTableTimeout = 50;
    setTimeout(() => {
      this.viewportScroller.scrollToAnchor('campaignsTable');
    }, showCampaignsTableTimeout);
  }

  public resetCampaignsFilterAndSelections(): void {
    this.selectedCampaigns = [];
    this.selectAll = false;
    this.displayUpdateTacticDialog = false;
    this.multipleTacticCampaignsCount = 0;
    this.underProcessingCampaignCount = 0;
    this.isAdvanceFilterApplied = false;
    this.resetFilterCriteriaForm();
    this.getCampaignsAndTactics();
  }

  private setTotalCampaignsOnCampaignTypeChange(): void {
    const selectedCampaignCount = this.campaignsCountByChannel.find(
      (campaign) => campaign.compositeDataSourceName === this.selectedDataSource
    );
    switch (this.selectedCampaignType) {
      case TaxonomyUiConstants.mappedCount:
        this.totalCampaigns = selectedCampaignCount?.mappedCount || 0;
        break;
      case TaxonomyUiConstants.unmappedCount:
        this.totalCampaigns = selectedCampaignCount?.unmappedCount || 0;
        break;
      case TaxonomyUiConstants.underProcessingCount:
        this.totalCampaigns = this.underProcessingCampaignCount;
        break;
      default:
        this.totalCampaigns = 0;
        break;
    }
  }

  public showOrHideMappingUpdateOverlayByCampaignSelection(): void {
    this.displayUpdateTacticDialog = !!this.selectedCampaigns.length;
    this.selectedCampaignsCount = this.selectedCampaigns.length || 0;

    if (!this.displayUpdateTacticDialog) {
      this.resetSelectedMappingDropdown();
    }

    if (this.selectedCampaignType !== TaxonomyUiConstants.unmappedCount) {
      this.selectedTactics =
        TaxonomyHelper.getNonDuplicateSelectedCampaignDimensionOptions(
          CampaignDimension.tactic,
          this.selectedCampaigns
        );
      this.selectedSegments =
        TaxonomyHelper.getNonDuplicateSelectedCampaignDimensionOptions(
          CampaignDimension.segment,
          this.selectedCampaigns
        );
      this.selectedChannels =
        TaxonomyHelper.getNonDuplicateSelectedCampaignDimensionOptions(
          CampaignDimension.channel,
          this.selectedCampaigns
        );
    }
  }

  public abortTacticUpdate(): void {
    this.selectedCampaigns = [];
    this.selectAll = false;
    this.showOrHideMappingUpdateOverlayByCampaignSelection();
  }

  public showNewTacticDialog(): void {
    if (this.selectedTacticForUpdate === TaxonomyUiConstants.createNewTactic) {
      this.displayNewTacticDialog = true;
    }
  }

  public showNewChannelDialog(): void {
    if (
      this.selectedChannelForUpdate === TaxonomyUiConstants.createNewChannel
    ) {
      this.displayNewChannelDialog = true;
    }
  }

  public createNewTactic(): void {
    const clientTactics = this.getClientTactics();
    const customTacticIndex = clientTactics.items.findIndex(
      (tactic) => tactic.custom
    );

    if (customTacticIndex > -1) {
      clientTactics.items[customTacticIndex] = {
        tactic: this.newTacticForm.value.newTactic,
        custom: true
      };
    } else {
      clientTactics.items.push({
        tactic: this.newTacticForm.value.newTactic,
        custom: true
      });
    }
    this.selectedTacticForUpdate = this.newTacticForm.value.newTactic;
    this.resetNewTacticFormAndCloseNewTacticDialog();
  }

  public createNewChannel(): void {
    const clientChannels = this.getClientChannels();
    const customTacticIndex = clientChannels.items.findIndex(
      (channel: IChannel) => channel.custom
    );

    if (customTacticIndex > -1) {
      clientChannels.items[customTacticIndex] = {
        channel: this.newChannelForm.value.newChannel,
        custom: true
      };
    } else {
      clientChannels.items.push({
        channel: this.newChannelForm.value.newChannel,
        custom: true
      });
    }
    this.selectedChannelForUpdate = this.newChannelForm.value.newChannel;
    this.newChannelForm.reset();
    this.displayNewChannelDialog = false;
  }

  private getClientTactics(): ITactics {
    return this.tactics.find(
      (parent) => parent.label === 'My Tactics'
    ) as ITactics;
  }

  private getClientChannels(): IChannels {
    return this.channels.find(
      (parent) => parent.label === 'My Channels'
    ) as IChannels;
  }

  public closeNewTacticDialog(): void {
    this.resetNewTacticFormAndCloseNewTacticDialog();
  }

  public closeNewChannelDialog(): void {
    if (
      this.selectedChannelForUpdate === TaxonomyUiConstants.createNewChannel
    ) {
      this.selectedChannelForUpdate = '';
    }
    this.newChannelForm.reset();
    this.displayNewChannelDialog = false;
  }

  public hideNewTacticDialog(): void {
    if (this.selectedTacticForUpdate === TaxonomyUiConstants.createNewTactic) {
      this.resetSelectedMappingDropdown();
    }
    this.resetNewTacticFormAndCloseNewTacticDialog();
  }

  private resetSelectedMappingDropdown(): void {
    this.selectedTacticForUpdate = '';
    this.selectedChannelForUpdate = '';
    this.selectedSegmentForUpdate = '';
  }

  private resetNewTacticFormAndCloseNewTacticDialog(): void {
    if (this.selectedTacticForUpdate === TaxonomyUiConstants.createNewTactic) {
      this.selectedTacticForUpdate = '';
    }
    this.newTacticForm.reset();
    this.displayNewTacticDialog = false;
  }

  private isTacticExisting(data: any): void {
    this.isNewTacticExisting = this.tactics.some((tactic) =>
      tactic.items.some((item) => item.tactic === data.newTactic)
    );
    if (
      this.isNewTacticExisting ||
      data.newTactic === 'Others' ||
      data.newTactic === 'Multiple Tactics'
    ) {
      this.isNewTacticExisting = true;
      this.newTacticForm.controls['newTactic']?.setErrors({});
    }
  }

  private isChannelExisting(data: any): void {
    this.isNewChannelExisting = this.channels.some((channel) =>
      channel.items.some((item: IChannel) => item.channel === data.newChannel)
    );
    if (this.isNewChannelExisting || data.newChannel === 'Others') {
      this.isNewChannelExisting = true;
      this.newChannelForm.controls['newChannel']?.setErrors({});
    }
  }

  public updateTactic(): void {
    const postBody = this.prepareUpdateTacticBody();
    this.loader[TaxonomyUiConstants.updateTactic] = true;
    this.loaderUiView = ViewType.loader;
    this.loaderTitle = `Work in progress`;
    this.loaderMessage = this.getLoaderMessageForUpdate();
    this.apiService.taxonomyApisOperations
      .updateCampaignsMapping(this.clientId, this.brandId, postBody)
      .then(() => {
        this.updateCampaignCountAfterUpdate();
        this.getCampaignsCountByStatus();
      })
      .catch((error) => {
        console.error('Error : ', error);
        this.loaderMessage = 'Failed to update campaigns';
        this.loaderUiView = ViewType.error;
      });
  }

  private prepareUpdateTacticBody(): IUpdateCampaignWithFilterCriteria {
    return {
      campaignType:
        TaxonomyUiConstants.campaignTypeFactory[this.selectedCampaignType],
      dataSourceId: this.selectedDataSource,
      channel: this.selectedChannelForUpdate,
      tactic: this.selectedTacticForUpdate,
      segment: this.selectedSegmentForUpdate,
      brandId: this.brandId,
      filterCriteria: this.getFilterCriteria()
    } as IUpdateCampaignWithFilterCriteria;
  }

  private isFilterApplied(): boolean {
    const filterCriteriaFormValues = this.filterCriteriaForm.getRawValue();
    return Object.keys(filterCriteriaFormValues).some(
      (scope) => filterCriteriaFormValues[`${scope}FilterValue`]
    );
  }

  private isAdvanceFiltersApplied(): boolean {
    const advanceFilters = this.advanceFiltersForm.getRawValue();
    return Object.keys(advanceFilters).some((dimension) => {
      const advanceFilter = advanceFilters[dimension];
      return advanceFilter.some(
        (filterName: Record<string, string>) => filterName[dimension]
      );
    });
  }

  private getFilterCriteria(): IFilterCriteria[] {
    if (!this.selectAll) {
      return TaxonomyHelper.prepareFilterCriteriaWithRecordId(
        this.selectedCampaigns
      );
    }
    if (this.isAdvanceFiltersApplied()) {
      return this.prepareFilterCriteriaForAdvanceFilters();
    }
    return this.prepareFilterCriteria();
  }

  private getLoaderMessageForUpdate(): string {
    if (this.removeTacticAction) {
      return `Unmapping tactic, segment & channel from ${this.selectedCampaigns.length.toLocaleString()} ${this.getCampaignString(
        this.selectedCampaigns.length
      )}`;
    }

    return `Currently mapping ${this.selectedCampaigns.length.toLocaleString()} ${this.getCampaignString(
      this.selectedCampaigns.length
    )}`;
  }

  private updateCampaignCountAfterUpdate(): void {
    Promise.all([
      this.apiService.taxonomyApisOperations.getCampaignCountByDataSourceAndChannel(
        this.clientId,
        this.brandId,
        this.selectedDataSource
      ),
      this.apiService.taxonomyApisOperations.getUnderProcessingCampaignsCount(
        this.clientId,
        this.brandId,
        this.selectedDataSource
      ),
      this.apiService.taxonomyApisOperations.getMultipleTacticCampaignsCount(
        this.clientId,
        this.brandId,
        this.selectedDataSource
      )
    ])
      .then(
        ([
          updatedCounts,
          updatedUnderProcessingCampaignCount,
          multipleTacticCampaignsCount
        ]) => {
          this.underProcessingCampaignCount =
            updatedUnderProcessingCampaignCount.count;
          this.multipleTacticCampaignsCount =
            multipleTacticCampaignsCount.count;
          this.updateCampaignTypeOptions(updatedCounts);
          this.campaignsCountByChannel.some((campaigns) => {
            if (
              campaigns.compositeDataSourceName === updatedCounts.dataSource
            ) {
              campaigns.mappedCount = updatedCounts.mappedCount;
              campaigns.unmappedCount = updatedCounts.unmappedCount;
              return true;
            }
            return false;
          });

          this.loaderTitle = 'Success!';
          this.loaderMessage = `${this.selectedCampaigns.length.toLocaleString()} ${this.getCampaignString(
            this.selectedCampaigns.length
          )} changes saved in Draft mode. Please click on 'Review Changes' at the top of the page to confirm correctness.`;

          if (this.removeTacticAction) {
            this.removeTacticAction = false;
          }

          this.loaderUiView = ViewType.success;
        }
      )
      .catch((error) => {
        console.error('Error : ', error);
        this.loaderMessage =
          'Failed to retrieving campaign mapping information';
        this.loaderUiView = ViewType.error;
      });
  }

  private updateCampaignTypeOptions(
    updatedCounts: IChannelsMappedAndUnmappedCount
  ): void {
    this.campaignTypeOptions = [
      {
        label: `Mapped Campaigns (${updatedCounts.mappedCount})`,
        value: TaxonomyUiConstants.mappedCount,
        disable: !updatedCounts.mappedCount
      },
      {
        label: `Unmapped Campaigns (${updatedCounts.unmappedCount})`,
        value: TaxonomyUiConstants.unmappedCount,
        disable: !updatedCounts.unmappedCount
      }
    ];
  }

  public removeCustomTactic(event: Event, tacticName: string): void {
    event.stopPropagation();
    const clientTactic = this.getClientTactics();
    clientTactic.items = clientTactic.items.filter(
      (tactic) => !(tactic.custom && tactic.tactic === tacticName)
    );
    this.selectedTacticForUpdate = '';
  }

  public removeCustomChannel(event: Event, channelName: string): void {
    event.stopPropagation();
    const clientChannel = this.getClientChannels();
    clientChannel.items = clientChannel.items.filter(
      (channel: IChannel) =>
        !(channel.custom && channel.channel === channelName)
    );
    this.selectedChannelForUpdate = '';
  }

  private resetTactic(): void {
    this.loader[TaxonomyUiConstants.updateTactic] = true;
    this.loaderTitle = 'Work in progress';
    this.loaderUiView = ViewType.loader;
    this.loaderMessage = this.getLoaderMessageForUpdate();
    this.apiService.taxonomyApisOperations
      .getUnmappedTactic()
      .then((unmappedTactic) => {
        this.removeTacticAction = true;
        this.selectedTacticForUpdate = unmappedTactic.tactic;
        this.selectedSegmentForUpdate = unmappedTactic.tactic;
        this.selectedChannelForUpdate = unmappedTactic.tactic;
        this.updateTactic();
      })
      .catch((error) => {
        console.error('Error : ', error);
        this.loaderTitle = 'Error!';
        this.loaderMessage = 'Failed to update campaigns';
        this.loaderUiView = ViewType.error;
      });
  }

  public confirmToResetMapping(): void {
    this.confirmationService.confirm({
      message: `This will attempt to use an automatic rule
      to reset and overwrite settings for <b>${
        this.selectedCampaignsCount
      }</b> ${this.getCampaignString(this.selectedCampaignsCount)}.
      If no existing rule applies, values will be reset to their defaults.`,
      accept: () => {
        this.resetTactic();
      },
      acceptLabel: 'Overwrite mapping',
      rejectLabel: 'Cancel'
    });
  }

  public handleCampaignCountLoaderClose(): void {
    delete this.loader['datasources'];
  }

  public handleCampaignDetailsLoaderClose(): void {
    delete this.loader['campaigns-tactic'];
    delete this.loader['campaigns'];
  }

  public handleUpdateCampaignDetailsLoaderContinue(): void {
    this.handleUpdateCampaignDetailsLoaderClose();
    this.resetCampaignsFilterAndSelections();
  }

  public handleUpdateCampaignDetailsLoaderClose(): void {
    delete this.loader[TaxonomyUiConstants.updateTactic];
  }

  public setCampaignsForTable(event: LazyLoadEvent): void {
    this.setCampaignsToCurrentPage(event);
    const campaignSize = this.campaigns.length;
    const { first = 0, rows = 0 } = event;
    if (
      (first / campaignSize >= TaxonomyUiConstants.loadFactor ||
        (first + rows) / campaignSize >= TaxonomyUiConstants.loadFactor) &&
      campaignSize > 0 &&
      campaignSize < this.totalCampaigns &&
      first + rows < this.totalCampaigns
    ) {
      this.loader['campaigns'] = true;
      this.loaderUiView = ViewType.loader;
      this.loaderTitle = `We're on it!`;
      this.loaderMessage =
        TaxonomyUiConstants.fetchingCampaignsLoaderMessage[
          this.selectedCampaignType
        ] || '';
      const postBody = this.getFilterCampaignsBody();
      this.apiService.taxonomyApisOperations
        .getCampaignsByDatasourceNChannel(this.clientId, this.brandId, postBody)
        .then((campaigns) => {
          campaigns.forEach((campaign) => {
            campaign.isSelectable =
              campaign.tactic !== TaxonomyUiConstants.multipleTactics;
          });
          this.campaigns = [...this.campaigns, ...campaigns];
          if (this.selectAll) {
            this.handleSelectedCampaignsOnSelectAll();
          }
          if (first + rows > campaignSize) {
            this.setCampaignsToCurrentPage(event);
          }
          this.loaderTitle = TaxonomyUiConstants.retrievingCampaigns;
          this.loaderMessage =
            campaigns.length === TaxonomyUiConstants.defaultLazyLoadingSize
              ? `Showing next 1,000 campaigns out of ${this.totalCampaigns.toLocaleString()}`
              : `Showing remaining ${campaigns.length.toLocaleString()} campaigns out of ${this.totalCampaigns.toLocaleString()}`;
        })
        .catch((error) => {
          console.error('Error: ', error.message);
          this.loaderTitle = 'Error!';
          this.loaderMessage = TaxonomyUiConstants.failureMessage;
          this.loaderUiView = ViewType.error;
        })
        .finally(() => {
          setTimeout(() => {
            this.loaderMessage = '';
            this.handleCampaignDetailsLoaderClose();
          }, TaxonomyUiConstants.waitTimeInMilliseconds);
        });
    }
  }

  private setCampaignsToCurrentPage(event: LazyLoadEvent): void {
    const campaignsViewed = event.first || 0;
    this.campaignsInView = this.campaigns.slice(
      campaignsViewed,
      campaignsViewed + (event.rows || 0) + 1
    );
  }

  private getCampaignString(campaignSize: number): string {
    return campaignSize > 1 ? 'campaigns' : 'campaign';
  }

  public handleSelectAllCampaigns(): void {
    this.handleSelectedCampaignsOnSelectAll();
    this.handleSelectedTacticsOnSelectAll();
  }

  private handleSelectedCampaignsOnSelectAll(): void {
    this.selectAll = true;
    this.displayUpdateTacticDialog = true;

    if (this.selectedCampaignType !== TaxonomyUiConstants.mappedCount) {
      this.selectedCampaigns = this.campaigns;
      this.selectedCampaignsCount = this.totalCampaigns;
    } else {
      this.selectedCampaigns = this.campaigns.filter(
        (campaign) => campaign.tactic !== 'Multiple Tactics'
      );
      this.selectedCampaignsCount =
        this.isFilterApplied() || this.isAdvanceFiltersApplied()
          ? this.selectedCampaigns.length
          : this.totalCampaigns - this.multipleTacticCampaignsCount;
    }
  }

  private handleSelectedTacticsOnSelectAll(): void {
    if (this.selectedCampaignType === TaxonomyUiConstants.mappedCount) {
      const clientTactics = this.tactics.find(
        (tactic) => tactic.label === 'My Tactics'
      );
      const isFilterAppied =
        this.isFilterApplied() || this.isAdvanceFiltersApplied();
      this.selectedTactics = isFilterAppied
        ? uniq(this.selectedCampaigns.map((campaign) => campaign.tactic))
        : (clientTactics?.items.map(
            (clientTactic) => clientTactic.tactic
          ) as string[]);
      this.selectedSegments = isFilterAppied
        ? uniq(this.selectedCampaigns.map((campaign) => campaign.segment))
        : (this.segments
            .find((segments) => segments.label === 'My Segments')
            ?.items.map((clientSegment) => clientSegment.segment) as string[]);
      this.selectedChannels = isFilterAppied
        ? uniq(this.selectedCampaigns.map((campaign) => campaign.channel))
        : (this.channels
            .find((channels) => channels.label === 'My Channels')
            ?.items.map(
              (clientChannel: IChannel) => clientChannel.channel
            ) as string[]);
    }

    if (
      this.selectedCampaignType === TaxonomyUiConstants.underProcessingCount
    ) {
      this.selectedTactics = uniq(
        this.selectedCampaigns.map((campaign) => campaign.tactic)
      );
      this.selectedSegments = uniq(
        this.selectedCampaigns.map((campaign) => campaign.segment)
      );
      this.selectedChannels = uniq(
        this.selectedCampaigns.map((campaign) => campaign.channel)
      );
    }
  }

  public searchTactic(searchValue: string): void {
    this.searchTactics = [];

    this.tactics.forEach((tactic) => {
      const matchedTactics = tactic.items
        .map((item) =>
          item.tactic &&
          item.tactic !== 'Create New Tactic' &&
          item.tactic.toLowerCase().includes(searchValue.toLowerCase())
            ? item.tactic
            : ''
        )
        .filter((item) => item);
      this.searchTactics.push(...matchedTactics);
    });
  }

  public searchChannel(searchValue: string): void {
    this.searchChannels = [];

    this.channels.forEach((channel) => {
      const matchedChannels = channel.items
        .map((item: IChannel) =>
          item.channel &&
          item.channel !== 'Create New Channel' &&
          item.channel.toLowerCase().includes(searchValue.toLowerCase())
            ? item.channel
            : ''
        )
        .filter((item) => item);
      this.searchChannels = uniq(matchedChannels);
    });
  }

  public addNewAdvanceFilter(fieldName: string) {
    const dimensionFormArray = this.advanceFiltersForm.controls[
      fieldName
    ] as UntypedFormArray;
    if (
      dimensionFormArray
        .getRawValue()
        [dimensionFormArray.length - 1][fieldName]?.trim()
    ) {
      this.addNewFilter(fieldName);
    }
  }

  public addNewFilter(
    fieldName: string,
    defaultFilterValue = '',
    defaultNegativeValue = false
  ): void {
    const dimensionFormArray = this.advanceFiltersForm.controls[
      fieldName
    ] as UntypedFormArray;
    dimensionFormArray.push(
      this.fb.group({
        [fieldName]: this.fb.control(
          defaultFilterValue ? defaultFilterValue : '',
          { updateOn: 'change' }
        ),
        [`is${fieldName}Negative`]: this.fb.control(defaultNegativeValue, {
          updateOn: 'change'
        })
      })
    );
  }

  public removeControl(fieldName: string, index: number): void {
    const advanceFilterFormArray = this.advanceFiltersForm.controls[
      fieldName
    ] as UntypedFormArray;
    advanceFilterFormArray.removeAt(index);
  }

  public applyAdvanceFilter(): void {
    this.isAdvanceFilterApplied = true;
    this.showAdvanceFilterDialog = false;
    this.resetFilterCriteriaForm();
    const advanceFilter = this.prepareFilterCriteriaForAdvanceFilters();
    if (advanceFilter.length) {
      this.filterCriteriaForm.disable();
    }
    this.abortTacticUpdate();
    this.getCampaigns();
  }

  public clearAdvanceFilter(): void {
    const taxonomyUIFactory = new TaxonomyUiFactory(this.fb);
    this.isAdvanceFilterApplied = false;
    this.advanceFiltersForm = taxonomyUIFactory.getAdvanceFilterForm();
    this.resetFilterCriteriaForm();
  }

  public saveAdvanceFilter(): void {
    const key = `${this.selectedDataSource}|${this.clientId}|${this.brandId}`;
    const advanceFilterValues = this.advanceFiltersForm.value;
    localStorage.setItem(key, JSON.stringify(advanceFilterValues));
    this.isAdvanceFilterSaved = true;
    setTimeout(() => {
      this.isAdvanceFilterSaved = false;
    }, TaxonomyUiConstants.waitTimeInMilliseconds);
  }

  private setOrResetAdvanceFilters(): void {
    const advanceFilterValues = JSON.parse(
      localStorage.getItem(
        `${this.selectedDataSource}|${this.clientId}|${this.brandId}`
      ) as string
    );
    if (advanceFilterValues) {
      this.clearAdvanceFilter();
      const keys = Object.keys(advanceFilterValues);
      keys.forEach((key) => {
        const values = advanceFilterValues[key];
        values.forEach(
          (value: Record<string, string | boolean>, index: number) => {
            if (index) {
              this.addNewFilter(
                key,
                value[key] as string,
                value[`is${key}Negative`] as boolean
              );
            }
          }
        );
      });
      this.advanceFiltersForm.patchValue(advanceFilterValues);
    } else {
      this.clearAdvanceFilter();
    }
  }

  public getCampaignsCountByStatus() {
    this.apiService.taxonomyApisOperations
      .getCampaignsCountByStatus(this.clientId, this.brandId)
      .then((counts: Record<string, number>) => {
        this.reviewNPublishCount = counts;
        this.actionButtonConfig.forEach((buttonConfig) => {
          if ('publish' === buttonConfig.value) {
            buttonConfig.disabled = !this.reviewNPublishCount[Status.drafted];
          } else {
            buttonConfig.disabled = false;
          }
        });
      })
      .catch((error) => {
        console.error(
          `Error occured while retrieving review and publish campaigns count`,
          error
        );
      });
  }

  public viewReviewAndPendingChanges(event: string) {
    switch (event) {
      case 'reviewChanges':
        this.actionButtonConfig = [
          TaxonomyUiConstants.goBackButtonConfig,
          TaxonomyUiConstants.publishButtonConfig
        ];
        this.isTaxonomyReviewPublishCard = true;
        this.getCampaignsCountByStatus();
        break;
      case 'goBack':
        this.actionButtonConfig = [
          TaxonomyUiConstants.reviewButtonConfig,
          TaxonomyUiConstants.publishButtonConfig
        ];
        this.isTaxonomyReviewPublishCard = false;
        break;
      case 'publish':
        this.confirmToPublish();
        break;
      default:
        break;
    }
  }

  public publishCampaignChanges() {
    this.loader[TaxonomyUiConstants.publishChanges] = true;
    this.loaderUiView = ViewType.loader;
    this.loaderTitle = `Work in progress`;
    this.loaderMessage = `Publishing campaign changes`;
    this.apiService.taxonomyApisOperations
      .publishCampaignChanges(this.clientId, this.brandId)
      .then((result) => {
        this.getCampaignsCountByStatus();
        const modifiedCount = result['modifiedCampaigns'];
        this.loaderTitle = 'Success!';
        this.loaderMessage = `${
          this.reviewNPublishCount[Status.drafted]
        } ${this.getCampaignString(Number(modifiedCount))} been published`;
        this.loaderUiView = ViewType.success;
      })
      .catch((error) => {
        console.error('Error occured while publishing changes', error);
        this.loaderTitle = 'Error!';
        this.loaderMessage = 'Failed to publish campaign changes';
        this.loaderUiView = ViewType.error;
      });
  }

  public handlePublishChangesLoaderClose() {
    delete this.loader[TaxonomyUiConstants.publishChanges];
    this.getCampaignsCount();
    if (this.selectedDataSource) {
      this.resetDatasourceSelectionAndCampaigns();
    }
    if (this.isTaxonomyReviewPublishCard) {
      this.taxonomyReviewNPublish?.clearAndLoadCampaigns();
    }
  }

  public confirmToPublish(): void {
    this.confirmDialogReviewButtonLabel = `Review ${
      this.reviewNPublishCount[Status.drafted]
    } changes`;
    this.confirmationService.confirm({
      message: `Are you sure? You have made ${
        this.reviewNPublishCount[Status.drafted]
      } changes. If necessary, you can review them before publishing.`,
      accept: () => {
        this.confirmDialogReviewButtonLabel = '';
        this.publishCampaignChanges();
      },
      reject: () => {
        this.confirmDialogReviewButtonLabel = '';
      },
      acceptLabel: 'Publish',
      rejectLabel: 'Cancel'
    });
  }

  private resetDatasourceSelectionAndCampaigns() {
    this.selectedDataSource = '';
    this.campaigns = [];
    this.campaignsInView = [];
    this.selectedCampaigns = [];
    this.displayUpdateTacticDialog = false;
  }

  public getToolTipMessageForCampaignDimensions(
    campaign: any,
    field: string
  ): string {
    if (campaign[field] && campaign[field] === 'Multiple Tactics') {
      return 'To update campaigns that are mapped to multiple tactics, please contact support.';
    }
    if (
      Object.keys(TaxonomyUiConstants.ruleDescriptionFieldConfig).includes(
        field
      )
    ) {
      const ruleDescField = (
        TaxonomyUiConstants.ruleDescriptionFieldConfig as any
      )[field];
      return this.getToolTipText(
        campaign[ruleDescField],
        campaign.modificationTimestamp
      );
    }
    return '';
  }

  private getToolTipText(ruleDesc: string, timestamp: string) {
    if (!ruleDesc || ruleDesc === 'Not Assigned') {
      return '';
    }
    if (ruleDesc.includes('Recommended')) {
      return ruleDesc;
    }
    if (ruleDesc === 'Taxonomy 2.0') {
      const dateNTime = TaxonomyHelper.convertToTimezone(
        timestamp,
        this.clientTimeZone
      ).split(', ');
      return `This value was set manually on ${dateNTime[0]} at ${dateNTime[1]}`;
    }
    return `This value was automatically set by ${ruleDesc}`;
  }

  public trackByCampaign(campaign: IVCampaign) {
    return campaign.tempRecordId;
  }

  public handleCampaignSelectionChanges(event: any) {
    this.selectedCampaigns = get(event, 'checked', []);
  }
}
